import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { tournamentApi } from '../services/api'
import { useAuth } from './AuthContext'

/**
 * Stay & Play portal i18n context — mirror of the tournament-web pattern but
 * fetches translations via /api/i18n/:langId since playstay-web doesn't host
 * its own static /lang/*.json files. The translation keys live in the SAME
 * shared dictionary served by Tournament-API (and published to
 * tournament-web/public/lang/) so we don't drift between products.
 *
 * Usage:
 *   const { t, langId, setLangId, languages } = useLanguage()
 *   <h1>{t('dashboard', 'Dashboard')}</h1>
 *
 * Lookup precedence: translations[key] || fallback || key.
 */
const LanguageContext = createContext(null)

export function LanguageProvider({ children }) {
  const { user } = useAuth()
  const [langId, setLangIdState] = useState(() => Number(localStorage.getItem('langId')) || 1)
  const [translations, setTranslations] = useState({})
  const [languages, setLanguages] = useState([])
  const [loading, setLoading] = useState(true)

  // Load available languages once (so the language switcher dropdown works)
  useEffect(() => {
    tournamentApi.get('/i18n/languages')
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setLanguages(data.map(l => ({
            id: l.Language_ID,
            tag: l.LanguageText_TagID,
            displayName: l.DisplayName,
          })))
        }
      })
      .catch(() => { /* fall back to {1:english,2:french} default below */ })
  }, [])

  // Honor the user profile's preferred language on login
  useEffect(() => {
    if (user?.Language_ID && user.Language_ID !== langId) {
      setLangIdState(user.Language_ID)
      localStorage.setItem('langId', String(user.Language_ID))
    }
  }, [user?.Language_ID])

  const loadTranslations = useCallback(() => {
    setLoading(true)
    tournamentApi.get(`/i18n/${langId}`)
      .then(({ data }) => setTranslations(data || {}))
      .catch(() => setTranslations({}))
      .finally(() => setLoading(false))
  }, [langId])

  useEffect(() => { loadTranslations() }, [loadTranslations])

  function setLangId(id) {
    setLangIdState(id)
    localStorage.setItem('langId', String(id))
  }

  /**
   * Translate a key. Returns the translated string, falling back to the
   * supplied English fallback (or the key itself if no fallback given).
   * Always pass a fallback so untranslated screens are usable in English.
   *
   * Optional third arg `vars` interpolates {placeholders}, e.g.
   *   t('sp_more_times', 'For {amount}, {count} more →', { amount, count })
   * so dynamic strings can be translated without string concatenation
   * (which can't be reordered per language).
   */
  function t(key, fallback, vars) {
    let s = (key && translations[key]) || fallback || key || ''
    if (vars) {
      for (const k in vars) s = s.split('{' + k + '}').join(String(vars[k] ?? ''))
    }
    return s
  }

  return (
    <LanguageContext.Provider value={{ langId, setLangId, t, languages, loading, reloadTranslations: loadTranslations }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    // Soft-fail: when a component is rendered outside the provider (e.g. tests
    // or pre-mount), return a passthrough t() so screens still show English.
    return {
      langId: 1,
      setLangId: () => {},
      t: (k, f, vars) => {
        let s = f || k || ''
        if (vars) for (const x in vars) s = s.split('{' + x + '}').join(String(vars[x] ?? ''))
        return s
      },
      languages: [],
      loading: false,
    }
  }
  return ctx
}
